import { graphql } from "gatsby"
import { CarouselProvider, Dot, Slide, Slider } from "pure-react-carousel"
import "pure-react-carousel/dist/react-carousel.es.css"
import React, { useLayoutEffect, useRef } from "react"
import { CarouselSlideFieldsFragment } from "../../../../graphql-types"
import { pushGtmEvent } from "../../../gtm/interaction-event"
import { useInternalExternalLink } from "../../../hooks"
import * as styles from "./carousel.module.scss"

type RenderProps = {
  slides: CarouselSlideFieldsFragment[]
  label: string
  previousLabel: string
  nextLabel: string
  pauseLabel: string
  playLabel: string
  locale: string
  defaultLocale: string
}

const Carousel: React.FC<RenderProps> = ({ slides, label, locale, defaultLocale }) => {
  const carouselRef = useRef<HTMLDivElement>(null)

  // pure-react-carousel still emits listbox/option attrs (aria-selected) after we override roles.
  useLayoutEffect(() => {
    const root = carouselRef.current
    if (!root) {
      return
    }
    root.querySelectorAll('[aria-roledescription="slide"]').forEach(slide => {
      slide.removeAttribute("aria-selected")
    })
  })

  return (
    <div ref={carouselRef}>
      <CarouselProvider
        className={styles.carousel}
        naturalSlideWidth={800}
        naturalSlideHeight={300}
        totalSlides={slides.length}
        isPlaying={true}
        infinite={true}
        interval={5000}
      >
        <div className={styles.controls}>
          {slides.map((slide, index) => (
            <Dot
              key={index}
              slide={index}
              aria-label={slide.image.alt || `${label} ${index + 1}`}
              onClick={() => pushGtmEvent("eventga", "Homepage", "Carousel", "Scroll Button Click")}
            />
          ))}
        </div>

        {/* Override pure-react-carousel listbox/option: links inside option violate nested-interactive. */}
        <Slider className={styles.sliderTray} role="region" aria-roledescription="carousel" aria-label={label}>
          {slides.map((slide, index) => {
            const link = useInternalExternalLink(slide.internalExternalLink[0], locale, defaultLocale)
            return (
              <Slide className={styles.slide} key={index} index={index} role="group" aria-roledescription="slide">
                <a href={link} onClick={() => pushGtmEvent("eventga", "Homepage", "Carousel", link)}>
                  <img src={slide.image.url} alt={slide.image.alt || ""} />
                </a>
              </Slide>
            )
          })}
        </Slider>
      </CarouselProvider>
    </div>
  )
}

export const fragment = graphql`
  fragment CarouselSlideFields on DatoCmsSlide {
    image {
      alt
      url
    }
    internalExternalLink {
      ...InternalExternalLinkFields
    }
  }
`

export default Carousel
